import React, { useEffect, useMemo, useRef, useState } from "react"
import { Video, Sparkles, Mic } from "lucide-react"
import type { CalendarEvent } from "../models/calendar.model"
import { getEventPalette } from "../utils/colorPalette"
import { layoutOverlappingEvents, layoutToCss } from "../utils/layoutEvents"
import { MIN_EVENT_MINUTES, MIN_EVENT_PX, PX_PER_HOUR } from "../utils/timeGrid"
import { useEventPopover } from "../hooks/useEventPopover"

/** Type icon — lets users tell a meeting from a webinar at the same time slot
 *  without changing the status-based colour palette. */
const TYPE_ICON: Record<CalendarEvent["type"], React.ReactNode> = {
  meeting: <Video className="w-3 h-3 shrink-0" />,
  webinar: <Sparkles className="w-3 h-3 shrink-0" />,
  call: <Mic className="w-3 h-3 shrink-0" />,
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)

function timeToMinutes(t: string) {
  const [h, m] = t.split(":").map(Number)
  return h * 60 + m
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

interface Props {
  currentDate: Date
  events: CalendarEvent[]
}

export default function DayView({ currentDate, events }: Props) {
  const today = new Date()
  const [nowMinutes, setNowMinutes] = useState(() => today.getHours() * 60 + today.getMinutes())
  const scrollRef = useRef<HTMLDivElement>(null)
  const { activeEventId, openPopover, closePopover, popoverPortal } = useEventPopover()

  useEffect(() => {
    const id = setInterval(() => {
      const n = new Date()
      setNowMinutes(n.getHours() * 60 + n.getMinutes())
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  // Scroll to 8 AM on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 7 * PX_PER_HOUR
    }
  }, [])

  const isToday = sameDay(currentDate, today)
  const dayEvents = events.filter(e => sameDay(e.date, currentDate))
  /** Column-packed layout for overlapping events (Google-Calendar style).
   *  Memoised so reordering only happens when the day's events actually
   *  change, not on every clock tick. */
  const laidOutEvents = useMemo(
    // Same floor the card height below uses, or the packer sees no overlap
    // between short back-to-back meetings and draws them on top of each other.
    () => layoutOverlappingEvents(dayEvents, { minDurationMin: MIN_EVENT_MINUTES }),
    [dayEvents],
  )
  const dateLabel = currentDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })

  const formatHour = (h: number) => {
    if (h === 0) return "12 AM"
    if (h < 12) return `${h} AM`
    if (h === 12) return "12 PM"
    return `${h - 12} PM`
  }

  return (
    <div className="flex flex-col h-full">
      {/* Day header */}
      <div className="shrink-0 flex items-center border-b border-border px-4 py-3 gap-4">
        <div className="text-left">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {currentDate.toLocaleDateString("en-US", { weekday: "long" })}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className={`text-3xl font-light leading-none ${isToday ? "w-12 h-12 flex items-center justify-center rounded-full bg-primary text-white font-medium text-2xl" : "text-foreground"
                }`}
            >
              {currentDate.getDate()}
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{dateLabel}</p>
      </div>

      {/* Time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto relative" onClick={closePopover}>
        <div className="flex" style={{ minHeight: `${24 * PX_PER_HOUR}px` }}>
          {/* Hour labels */}
          <div className="w-16 shrink-0 text-right pr-3 relative select-none">
            {HOURS.map(h => (
              <div
                key={h}
                style={{ height: PX_PER_HOUR, marginTop: h === 0 ? 0 : 0 }}
                className="flex items-start justify-end pt-1"
              >
                {h > 0 && (
                  <span className="text-[10px] text-muted-foreground -translate-y-2">
                    {formatHour(h)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Grid column */}
          <div className="flex-1 relative border-l border-border">
            {/* Hour lines */}
            {HOURS.map(h => (
              <div
                key={h}
                style={{ top: h * PX_PER_HOUR }}
                className="absolute left-0 right-0 border-t border-border"
              />
            ))}
            {/* Half-hour lines */}
            {HOURS.map(h => (
              <div
                key={`half-${h}`}
                style={{ top: h * PX_PER_HOUR + PX_PER_HOUR / 2 }}
                className="absolute left-0 right-0 border-t border-dashed border-border/30"
              />
            ))}

            {/* Current time line */}
            {isToday && (
              <div
                style={{ top: nowMinutes * (PX_PER_HOUR / 60) }}
                className="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
              >
                <div className="w-2.5 h-2.5 rounded-full bg-red-500 -translate-x-1 shrink-0" />
                <div className="flex-1 border-t border-red-500" />
              </div>
            )}

            {/* Events — column-packed (Google-Calendar-style overlap layout)
                using the existing soft palette + status-coloured left border.
                Each block carries a small type icon so a meeting and webinar
                at the same time slot are still distinguishable at a glance. */}
            {laidOutEvents.map(({ event: ev, layout }) => {
              const startMin = timeToMinutes(ev.startTime)
              const endMin = timeToMinutes(ev.endTime)
              const top = startMin * (PX_PER_HOUR / 60)
              const height = Math.max((endMin - startMin) * (PX_PER_HOUR / 60), MIN_EVENT_PX)
              const palette = getEventPalette(ev)
              const isActive = activeEventId === ev.id
              const isDarkMode = document.documentElement.classList.contains('dark')
              const { left, width } = layoutToCss(layout, { outerInsetPx: 6, gapPx: 4 })
              return (
                <div
                  key={ev.id}
                  onClick={e => {
                    e.stopPropagation()
                    if (isActive) {
                      closePopover()
                    } else {
                      openPopover(ev, e.currentTarget as HTMLElement)
                    }
                  }}
                  style={{
                    top,
                    height,
                    left,
                    width,
                    background: isDarkMode ? palette.darkLight : palette.light,
                    borderLeft: `3px solid ${palette.solid}`,
                    color: isDarkMode ? palette.darkText : palette.text,
                  }}
                  className={`absolute rounded-lg px-2 py-1 cursor-pointer transition-all overflow-hidden z-10 group ${isActive ? "ring-2 ring-offset-1 ring-primary/50" : ""
                    }`}
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="opacity-70 shrink-0">{TYPE_ICON[ev.type]}</span>
                    <p className="text-[11px] font-semibold leading-tight truncate">{ev.title}</p>
                  </div>
                  {height > 30 && (
                    <p className="text-[10px] leading-tight truncate opacity-80 pl-4">
                      {ev.startTime} – {ev.endTime}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {popoverPortal}
    </div>
  )
}
